import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/* ─── Re-exports (pass-through) ─── */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

/* ─── Overlay ─── */
const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/* ─── Escape fallback ───────────────────────────────────────────────────────
 *
 * Radix's DismissableLayer decides which open layer owns Escape from `index === layers.size - 1`,
 * where `index` is captured in a RENDER closure and reaches the document keydown listener through
 * `useCallbackRef` — whose `callbackRef.current = callback` assignment is a PASSIVE effect. A
 * freshly-mounted layer therefore carries `index === -1` until (1) its mount effect adds its node to
 * the module-global layer set, (2) `dispatchUpdate()` forces a re-render, and (3) that render's
 * passive effects flush. Until (3) lands, EVERY layer computes `isHighestLayer === false` and the
 * Escape is dropped with no error, no console output and no visual symptom. It is a settle race, not
 * a nesting bug: it reproduces on a single un-nested dialog, and hand-written nested dialogs pass.
 *
 * This is a FALLBACK, never a replacement. Radix registers its own Escape listener in the CAPTURE
 * phase on the document; ours listens in the BUBBLE phase, which the DOM guarantees runs strictly
 * afterwards regardless of registration order. So whenever Radix handled the key at all it has
 * already set `defaultPrevented`, and we bail. We only ever run in the window Radix dropped.
 *
 * Registration uses `useLayoutEffect`, not `useEffect`, on purpose: a passive effect is precisely
 * the delivery mechanism that made Radix's own check race. Layout effects flush synchronously
 * inside the commit, before the browser can deliver the keydown that follows it.
 */

interface DialogEscapeGuard {
  /** The consumer's `onEscapeKeyDown`, kept current so conditional opt-outs read fresh state. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** Dismiss through Radix's own state transition rather than around it. */
  close: () => void;
  /** The rendered `[role="dialog"]` element, or null while unmounted. */
  element: () => HTMLElement | null;
}

/** Mounted dialog contents, innermost last. */
const dialogEscapeGuards: DialogEscapeGuard[] = [];

function handleFallbackEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.defaultPrevented) return;

  const guard = dialogEscapeGuards[dialogEscapeGuards.length - 1];
  if (!guard) return;

  // Ignore a dialog that is already playing its exit animation — it is still in the DOM but is no
  // longer the layer the user is looking at.
  const element = guard.element();
  if (!element || element.getAttribute('data-state') !== 'open') return;

  // Only the topmost dialog may close, and "topmost" is judged across EVERY open Radix dialog —
  // including ones that use the Radix primitives directly (ImageLightbox, the settings sheets) and
  // so never enter this stack. Portals are appended to the body in mount order, so the last match in
  // document order is the newest layer. If something newer is open, this is not our key to take.
  //
  // 🔴 "Newest layer" is NOT the same question as "newest dialog". Radix dismissable layers carry
  // several different roles, and a layer opened INSIDE a dialog portals to the body AFTER it, so it
  // is genuinely newer even though it is not a dialog. Judging only `[role="dialog"]` made this
  // guard mistake such a layer's Escape for its own and close the whole dialog underneath it —
  // measured 8/8 on "Add Tournament" with the format `Select` open, discarding a half-filled form.
  // `Select.jsx` is a Radix Popover with `role="listbox"` (Select.jsx:239) and is rendered inside
  // `DialogContent` at 60+ sites, so this was live, not theoretical. Pre-fix it was invisible only
  // because a tight Escape did nothing at all and the dialog survived by accident.
  //
  // `tooltip` is deliberately EXCLUDED: a tooltip is not user-dismissed and is frequently visible
  // while the pointer rests, so counting it would let a hover silently swallow the dialog's Escape.
  const openLayers = document.querySelectorAll(
    '[role="dialog"][data-state="open"],' +
    '[role="alertdialog"][data-state="open"],' +
    '[role="menu"][data-state="open"],' +
    '[role="listbox"][data-state="open"]',
  );
  if (openLayers[openLayers.length - 1] !== element) return;

  // Respect a deliberate opt-out, using the same mechanism and the same semantics Radix uses: hand
  // the consumer's handler the real event and stand down if it calls preventDefault(). This is what
  // keeps a sensitive-action success state (a revealed one-time password) from being dismissed —
  // see ResetPasswordDialog. Conditional opt-outs (`if (submitting) e.preventDefault()`) work too,
  // because the handler is refreshed in a layout effect on every render.
  guard.onEscapeKeyDown?.(event);
  if (event.defaultPrevented) return;

  // Claim the key so no layer underneath acts on the same press.
  event.preventDefault();
  guard.close();
}

function pushDialogEscapeGuard(guard: DialogEscapeGuard) {
  dialogEscapeGuards.push(guard);
  if (dialogEscapeGuards.length === 1) {
    document.addEventListener('keydown', handleFallbackEscape);
  }
}

function popDialogEscapeGuard(guard: DialogEscapeGuard) {
  const index = dialogEscapeGuards.lastIndexOf(guard);
  if (index !== -1) dialogEscapeGuards.splice(index, 1);
  if (dialogEscapeGuards.length === 0) {
    document.removeEventListener('keydown', handleFallbackEscape);
  }
}

/**
 * Rendered inside `DialogPrimitive.Content`, so it mounts exactly while the content is present and
 * unmounts with it. The hidden `DialogClose` is how the fallback dismisses: it routes through
 * Radix's own `onOpenChange`, leaving portal, focus trap, focus restore and ARIA untouched.
 */
const DialogEscapeFallback: React.FC<{
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
}> = ({ onEscapeKeyDown }) => {
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const guardRef = React.useRef<DialogEscapeGuard>();

  if (!guardRef.current) {
    guardRef.current = {
      onEscapeKeyDown,
      close: () => closeRef.current?.click(),
      element: () => (closeRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null,
    };
  }

  React.useLayoutEffect(() => {
    guardRef.current!.onEscapeKeyDown = onEscapeKeyDown;
  });

  React.useLayoutEffect(() => {
    const guard = guardRef.current!;
    pushDialogEscapeGuard(guard);
    return () => popDialogEscapeGuard(guard);
  }, []);

  return <DialogClose ref={closeRef} tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} />;
};

/* ─── Content Size Variants ─── */
const dialogContentVariants = cva(
  'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-card border border-border rounded-xl shadow-xl w-full max-h-[90vh] overflow-y-auto data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
  {
    variants: {
      size: {
        sm: 'max-w-md',       // 448px
        default: 'max-w-lg',  // 512px
        lg: 'max-w-2xl',      // 672px
        xl: 'max-w-4xl',      // 896px
        full: 'max-w-[calc(100vw-2rem)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

/* ─── Content ─── */
export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  hideCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, size, hideCloseButton = false, children, ...props }, ref) => {
  // The close affordance is icon-only, so this sr-only label is the ONLY name a screen reader
  // gets for it. It was hardcoded English, which read as "Close" inside fully-Arabic and
  // fully-French dialogs app-wide. `Close` already exists in en/ar/fr common.json.
  const { t } = useTranslation();

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ size }), className)}
        {...props}
      >
        {children}
        {/* `onEscapeKeyDown` stays in `props` above so Radix keeps handling it on the normal path;
            the fallback only re-uses it to detect a deliberate opt-out. */}
        <DialogEscapeFallback onEscapeKeyDown={props.onEscapeKeyDown} />
        {!hideCloseButton && (
          <DialogClose className="absolute top-4 ltr:right-4 rtl:left-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">{t('Close')}</span>
          </DialogClose>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

/* ─── Header ─── */
const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 border-b border-border', className)} {...props} />
  )
);
DialogHeader.displayName = 'DialogHeader';

/* ─── Body ─── */
const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6', className)} {...props} />
  )
);
DialogBody.displayName = 'DialogBody';

/* ─── Footer ─── */
const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('p-6 border-t border-border flex items-center justify-end gap-3', className)}
      {...props}
    />
  )
);
DialogFooter.displayName = 'DialogFooter';

/* ─── Title (wraps Radix for ARIA) ─── */
const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-semibold text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

/* ─── Description (wraps Radix for ARIA) ─── */
const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
export default Dialog;
